import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import SEO from '../components/SEO';
import { getSegmentBySlug } from '../data/transitions';
import { STRIPE_CHECKOUT_URL, DISCOVERY_CALL_URL } from '../constants/site';

const TransitionSegmentPage = () => {
  const { segment } = useParams();
  const data = getSegmentBySlug(segment);
  useEffect(() => { window.scrollTo(0, 0); }, [segment]);

  if (!data) return <Navigate to="/transitions" replace />;

  return (
    <div data-testid={`transition-segment-${data.slug}`} className="min-h-screen">
      <SEO title={data.metaTitle} description={data.metaDescription} path={`/transitions/${data.slug}`} noindex={data.hidden === true} />

      {/* HERO */}
      <section className="bg-[#FDFBF7] py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <span className="overline inline-block mb-6">Transition guide</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6 max-w-3xl mx-auto">
            {data.label} → Private Aviation.
          </h1>
          <p className="text-lg text-[#1A1A1A] leading-relaxed max-w-2xl mx-auto mb-8">{data.heroLine}</p>
          <div className="flex items-center justify-center gap-4 max-w-lg mx-auto text-left">
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_gate33-preview/artifacts/dq20hpml_Screenshot%202026-07-18%20at%2020.51.57.png"
              alt="Nicolas Perot dos Santos — Gate 33"
              className="w-14 h-14 rounded-full object-cover border-2 border-[#C5A880] flex-shrink-0"
            />
            <p className="text-xs sm:text-sm text-[#1A1A1A] leading-snug">
              <span className="font-semibold">Nicolas Perot dos Santos</span> — Qatar Airways, Etihad, today flying <span className="font-semibold">Bombardier Global & Challenger series</span>.
            </p>
          </div>
        </div>
      </section>

      {/* STRENGTHS + GAPS */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="bg-[#FDFBF7] border border-[#E5E0D8] p-8">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">What you already bring</span>
            <h2 className="font-heading text-2xl md:text-3xl text-[#0F0F0F] mt-3 mb-6 leading-tight">Recognised by operators from day one.</h2>
            <ul className="space-y-5">
              {data.strengths.map((s) => (
                <li key={s.title} className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-[#C5A880] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
                  <div>
                    <p className="font-semibold text-[#0F0F0F] leading-snug">{s.title}</p>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed mt-1">{s.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-[#0F0F0F]/15 p-8">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#5A5A5A] font-semibold">What's genuinely missing</span>
            <h2 className="font-heading text-2xl md:text-3xl text-[#0F0F0F] mt-3 mb-6 leading-tight">Named honestly, so you know what to close.</h2>
            <ul className="space-y-4">
              {data.gaps.map((g, i) => (
                <li key={i} className="flex items-start gap-3">
                  <XCircle size={18} className="text-[#5A5A5A] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
                  <span className="text-sm text-[#1A1A1A] leading-relaxed">{g}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 20/80 */}
      <section className="section-dark py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">The 20/80 line</span>
          <p className="font-heading text-2xl sm:text-3xl text-[#FDFBF7] leading-snug mt-4 italic">
            &ldquo;{data.twentyEighty}&rdquo;
          </p>
        </div>
      </section>

      {/* HOW GATE 33 CLOSES THE GAP */}
      <section className="section-padding bg-[#FDFBF7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <span className="overline inline-block mb-4">How Gate 33 closes the gap</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#0F0F0F] leading-tight mb-6">3 live online days. Delivered by a working corporate flight attendant.</h2>
          <p className="text-[#5A5A5A] leading-relaxed mb-10">
            Every module is live with Nicolas — no recordings, no self-paced content. The training rebuilds the operational 80% first, then re-frames your background in the language operators are actually screening for.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href={STRIPE_CHECKOUT_URL} target="_blank" rel="noopener noreferrer" data-testid="transition-reserve-cta" className="btn-primary inline-flex items-center gap-2">
              Reserve my seat — €600 <ArrowRight size={16} />
            </a>
            <a href={DISCOVERY_CALL_URL} target="_blank" rel="noopener noreferrer" data-testid="transition-discovery-cta" className="btn-secondary inline-flex items-center gap-2">
              Book a free Discovery Call
            </a>
          </div>
          <Link to="/academy" className="mt-6 inline-block text-xs uppercase tracking-[0.18em] text-[#C5A880] font-semibold hover:underline">
            See the 3-Day Online Training →
          </Link>
        </div>
      </section>

      {/* TESTIMONIAL */}
      {data.testimonial && (
        <section className="bg-white border-y border-[#E5E0D8] py-14">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <blockquote className="border-l-2 border-[#C5A880] pl-6 md:pl-8">
              <p className="font-heading text-xl md:text-2xl text-[#0F0F0F] leading-relaxed italic mb-4">&ldquo;{data.testimonial.quote}&rdquo;</p>
              <footer className="flex items-center gap-3 text-sm text-[#5A5A5A]">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#C5A880] text-[#0F0F0F] text-xs font-semibold">{data.testimonial.initials}</span>
                <span>
                  <span className="font-semibold text-[#0F0F0F]">{data.testimonial.name}</span>
                  <span className="text-[#5A5A5A]"> · {data.testimonial.background}</span>
                </span>
              </footer>
            </blockquote>
          </div>
        </section>
      )}

      {/* ROLES OR LINKS */}
      {data.roles && data.roles.length > 0 && (
        <section className="section-padding bg-white">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <span className="overline inline-block mb-4">Roles in this family</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#0F0F0F] leading-tight mb-8">Pick the closest match to your current job.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.roles.map((r) => (
                <Link
                  key={r.slug}
                  to={`/transitions/${data.slug}/${r.slug}`}
                  data-testid={`transition-role-link-${r.slug}`}
                  className="flex items-center justify-between gap-4 p-5 bg-[#FDFBF7] border border-[#E5E0D8] hover:border-[#C5A880] transition-colors group"
                >
                  <span className="text-[#0F0F0F] font-medium">{r.title}</span>
                  <ArrowRight size={16} className="text-[#C5A880] group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.linksToExisting && (
        <section className="section-padding bg-white">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <span className="overline inline-block mb-4">{data.linksToExisting.label}</span>
            <p className="text-[#5A5A5A] leading-relaxed mb-8 max-w-2xl">{data.linksToExisting.note}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.linksToExisting.airlines.map((a) => (
                <Link
                  key={a.slug}
                  to={`${data.linksToExisting.hrefBase}${a.slug}/en`}
                  className="flex items-center justify-between gap-2 p-4 bg-[#FDFBF7] border border-[#E5E0D8] hover:border-[#C5A880] text-sm text-[#0F0F0F] transition-colors"
                >
                  <span className="font-medium">{a.name}</span>
                  <ArrowRight size={14} className="text-[#C5A880]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default TransitionSegmentPage;
